import { computed, ref } from 'vue';
import SurveyFormsAPI from 'dashboard/api/surveyForms';
import {
  filterForms,
  normalizeForm,
  responseRate,
} from '../helper/surveyFormHelper';

/**
 * List-level state for the survey forms screen.
 *
 * The screen talks to `dashboard/api/surveyForms` directly (no vuex module) so
 * it stays independent from the store surface owned by other modules. Filtering
 * happens in memory: a settings-sized collection is fetched once and every
 * keystroke is instant.
 */
export const extractCollection = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.payload)) return payload.payload;
  return [];
};

export const extractRecord = payload => {
  if (!payload || typeof payload !== 'object') return {};
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

export const apiErrorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data?.errors?.[0] ||
  error?.message ||
  '';

export function useSurveyForms() {
  const forms = ref([]);
  const total = ref(0);
  const isFetching = ref(false);
  const hasLoaded = ref(false);
  const hasFetchError = ref(false);
  const fetchErrorMessage = ref('');
  // `${action}:${id}` while a row-level mutation is in flight.
  const pendingAction = ref('');

  const filters = ref({ kind: '', active: '', q: '' });

  const filteredForms = computed(() => filterForms(forms.value, filters.value));

  const hasFilters = computed(() =>
    Boolean(
      filters.value.kind || filters.value.active || filters.value.q.trim()
    )
  );

  const isEmpty = computed(
    () => hasLoaded.value && !isFetching.value && forms.value.length === 0
  );

  const isFilteredEmpty = computed(
    () =>
      hasLoaded.value &&
      !isFetching.value &&
      forms.value.length > 0 &&
      filteredForms.value.length === 0
  );

  const summary = computed(() => {
    const list = forms.value;
    const sent = list.reduce(
      (acc, form) => acc + (Number(form.responses_count) || 0),
      0
    );
    const submitted = list.reduce(
      (acc, form) => acc + (Number(form.submitted_count) || 0),
      0
    );

    return {
      total: total.value || list.length,
      active: list.filter(form => form.active).length,
      sent,
      submitted,
      rate: sent ? Math.round((submitted / sent) * 100) : 0,
    };
  });

  const setFilter = (key, value) => {
    filters.value = { ...filters.value, [key]: value };
  };

  const resetFilters = () => {
    filters.value = { kind: '', active: '', q: '' };
  };

  const fetchForms = async () => {
    isFetching.value = true;
    hasFetchError.value = false;
    fetchErrorMessage.value = '';

    try {
      const { data } = await SurveyFormsAPI.get();
      forms.value = extractCollection(data).map(normalizeForm);
      total.value = Number(data?.meta?.count) || forms.value.length;
      hasLoaded.value = true;
    } catch (error) {
      hasFetchError.value = true;
      fetchErrorMessage.value = apiErrorMessage(error);
    } finally {
      isFetching.value = false;
    }
  };

  const upsertLocal = form => {
    const normalized = normalizeForm(form);
    const index = forms.value.findIndex(item => item.id === normalized.id);
    if (index === -1) forms.value = [normalized, ...forms.value];
    else forms.value.splice(index, 1, normalized);
    return normalized;
  };

  const runAction = async (action, id, request) => {
    pendingAction.value = `${action}:${id}`;
    try {
      const result = await request();
      return { ok: true, data: result };
    } catch (error) {
      return { ok: false, message: apiErrorMessage(error) };
    } finally {
      pendingAction.value = '';
    }
  };

  const isPending = (action, id) => pendingAction.value === `${action}:${id}`;

  const duplicateForm = form =>
    runAction('duplicate', form.id, async () => {
      const { data } = await SurveyFormsAPI.duplicate(form.id);
      return upsertLocal(extractRecord(data));
    });

  const toggleActive = form =>
    runAction('toggle', form.id, async () => {
      const { data } = await SurveyFormsAPI.update(form.id, {
        active: !form.active,
      });
      return upsertLocal(extractRecord(data));
    });

  const deleteForm = form =>
    runAction('delete', form.id, async () => {
      await SurveyFormsAPI.delete(form.id);
      forms.value = forms.value.filter(item => item.id !== form.id);
      total.value = Math.max(0, total.value - 1);
      return form;
    });

  return {
    forms,
    total,
    filters,
    filteredForms,
    hasFilters,
    isFetching,
    hasLoaded,
    hasFetchError,
    fetchErrorMessage,
    isEmpty,
    isFilteredEmpty,
    summary,
    pendingAction,
    isPending,
    setFilter,
    resetFilters,
    fetchForms,
    duplicateForm,
    toggleActive,
    deleteForm,
    responseRate,
  };
}

export default useSurveyForms;
